
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { toast } from "sonner";
import { useMapsLinkProcessor } from '../hooks/useMapsLinkProcessor';
import LinkInputForm from './LinkInputForm';
import LinkHelpInfo from './LinkHelpInfo';
import LinkResetButton from './LinkResetButton';
import LinkWarningMessage from './LinkWarningMessage';

interface LinkInputSectionProps {
  onLinkProcessed: (success: boolean, data?: any) => void;
  isProcessingLink: boolean;
  setIsProcessingLink: (loading: boolean) => void;
  onReset?: () => void;
  showReset?: boolean;
}

const LinkInputSection: React.FC<LinkInputSectionProps> = ({ 
  onLinkProcessed, 
  isProcessingLink, 
  setIsProcessingLink,
  onReset,
  showReset = false
}) => {
  const [link, setLink] = useState<string>('');
  const { error, processLink, resetError } = useMapsLinkProcessor();

  const handleLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsProcessingLink(true);
    
    await processLink(
      link,
      (data) => onLinkProcessed(true, data),
      () => onLinkProcessed(false)
    );
    
    setIsProcessingLink(false);
  };

  const handleReset = () => {
    setLink('');
    resetError();
    if (onReset) {
      onReset();
    }
    toast.info("Form reset. You can now enter a new Google Maps link.");
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-lg flex items-center justify-between">
          <div className="flex items-center">
            Prefill with Google Maps
            <LinkHelpInfo />
          </div>
          {showReset && (
            <LinkResetButton onReset={handleReset} />
          )}
        </CardTitle>
        <CardDescription>
          {showReset ? (
            "Want to add a different place? Click 'New Place' to reset the form and enter a new Google Maps link."
          ) : (
            "Paste a Google Maps link to the terrace, and we'll use AI to fill in the details. Both full and shortened Google Maps URLs are supported."
          )}
        </CardDescription>
      </CardHeader>
      {!showReset && (
        <>
          <CardContent>
            <LinkInputForm
              link={link}
              setLink={setLink}
              onSubmit={handleLinkSubmit}
              isProcessing={isProcessingLink}
              error={error}
            />
          </CardContent>
        </>
      )}
    </Card>
  );
};

export default LinkInputSection;
